import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageTemplateDialog from "../components/SignageTemplateDialog";
import SignageSlideDialog from "../components/SignageSlideDialog";
import { useCan } from "../auth/AuthContext";
import { tokens } from "../theme";
import { foyerApi, signageScopeKey, type SignageScopeRef } from "../http/foyer";
import { isOwnedBy, ownerLabel } from "../utils/signageItems";
import type {
  SignageTemplate,
  SignageTemplateInstance,
} from "@shared/types/signage-template";

/**
 * Templates & slides - the two halves of the signage template system, on one
 * page because they are one job seen from two sides.
 *
 * The TABS are not cosmetic: they are the two tiers. A user with
 * `canManageSignage` and nothing else sees Slides, can build one, and finds the
 * Templates tab read-only - no New, no Edit, no Delete. A designer sees both.
 * That is the same shape the Channels page uses for `BE-40`: a customer browses
 * the catalogue and nothing on their screen suggests they could add to it.
 */

export default function SignageTemplatesPage({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeIdent = signageScopeKey(scope);
  const canDesign = useCan("canAuthorSignageTemplates");

  const [tab, setTab] = useState<"slides" | "templates">("slides");
  const [templates, setTemplates] = useState<SignageTemplate[]>([]);
  const [slides, setSlides] = useState<SignageTemplateInstance[]>([]);
  const [tick, setTick] = useState(0);

  const [templateOpen, setTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<SignageTemplate | null>(null);
  const [slideOpen, setSlideOpen] = useState(false);
  const [editingSlide, setEditingSlide] =
    useState<SignageTemplateInstance | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      foyerApi.listSignageTemplatesIn(scope),
      foyerApi.listSignageSlidesIn(scope),
    ])
      .then(([t, s]) => {
        if (!alive) return;
        setTemplates(t);
        setSlides(s);
      })
      .catch(() => {
        if (!alive) return;
        setTemplates([]);
        setSlides([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeIdent, tick]);

  const byId = useMemo(
    () => new Map(templates.map((t) => [t.id, t] as const)),
    [templates]
  );

  const saveTemplate = async (patch: Partial<SignageTemplate>) => {
    setSaving(true);
    setError(null);
    try {
      if (editingTemplate) {
        await foyerApi.updateSignageTemplateIn(
          scope,
          editingTemplate.id,
          patch
        );
      } else {
        await foyerApi.createSignageTemplateIn(scope, patch);
      }
      setTemplateOpen(false);
      setTick((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const saveSlide = async (body: {
    name: string;
    templateId: string;
    values: Record<string, string>;
  }) => {
    setSaving(true);
    setError(null);
    try {
      if (editingSlide) {
        await foyerApi.updateSignageSlideIn(scope, editingSlide.id, {
          name: body.name,
          values: body.values,
        });
      } else {
        await foyerApi.createSignageSlideIn(scope, body);
      }
      setSlideOpen(false);
      setTick((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async (id: string) => {
    try {
      await foyerApi.deleteSignageTemplateIn(scope, id);
      setTick((t) => t + 1);
    } catch {
      /*
       * A 409 means slides are built from it. The server counts them and
       * refuses, because deleting would silently empty every one of those
       * slides - and they sit in other people's playlists, possibly at another
       * layer. Said here rather than swallowed.
       */
      setError(
        "That template is still used by slides. Delete or repoint those first."
      );
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Templates"
        subtitle="Designs somebody makes once, and the slides filled in from them. To put a slide on a screen, add it to a playlist (Plan content → Playlists → Add slide)."
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as typeof tab)}
        sx={{ mb: 2 }}
      >
        <Tab value="slides" label={`Slides (${slides.length})`} />
        <Tab value="templates" label={`Templates (${templates.length})`} />
      </Tabs>

      {error && (
        <Typography sx={{ mb: 2, fontSize: 13, color: tokens.error }}>
          {error}
        </Typography>
      )}

      {tab === "slides" ? (
        <Stack gap={1}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={templates.length === 0}
              onClick={() => {
                setEditingSlide(null);
                setSlideOpen(true);
              }}
            >
              New slide
            </Button>
          </Stack>

          {slides.length === 0 ? (
            <Empty>
              {templates.length === 0
                ? // Offer the action that is POSSIBLE - the rule the playlist
                  // dialog already follows when a scope has no playlists.
                  "No templates to fill in yet. Somebody with template design rights makes one first."
                : "No slides yet. Pick a template and fill it in."}
            </Empty>
          ) : (
            slides.map((slide) => {
              const owned = isOwnedBy(slide, scope);
              return (
                <Row key={slide.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {slide.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {byId.get(slide.templateId)?.name ?? "Deleted template"}
                    </Typography>
                  </Box>
                  {!owned && <Chip size="small" label={ownerLabel(slide)} />}
                  {owned && (
                    <>
                      <IconButton
                        size="small"
                        aria-label={`Edit ${slide.name}`}
                        onClick={() => {
                          setEditingSlide(slide);
                          setSlideOpen(true);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${slide.name}`}
                        onClick={() =>
                          void foyerApi
                            .deleteSignageSlideIn(scope, slide.id)
                            .then(() => setTick((t) => t + 1))
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Row>
              );
            })
          )}
        </Stack>
      ) : (
        <Stack gap={1}>
          {canDesign && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateOpen(true);
                }}
              >
                New template
              </Button>
            </Stack>
          )}

          {templates.length === 0 ? (
            <Empty>
              {canDesign
                ? "No templates yet. Design one, then anyone who authors signage can fill it in."
                : "No templates yet. These are made by whoever holds template design rights."}
            </Empty>
          ) : (
            templates.map((t) => {
              const owned = isOwnedBy(t, scope);
              const used = slides.filter((s) => s.templateId === t.id).length;
              return (
                <Row key={t.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {t.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {t.canvas.width > t.canvas.height
                        ? "Landscape"
                        : "Portrait"}{" "}
                      · {t.fields.length}{" "}
                      {t.fields.length === 1 ? "field" : "fields"}
                      {used > 0 && ` · ${used} slide${used === 1 ? "" : "s"}`}
                    </Typography>
                  </Box>
                  {!owned && <Chip size="small" label={ownerLabel(t)} />}
                  {canDesign && owned && (
                    <>
                      <IconButton
                        size="small"
                        aria-label={`Edit ${t.name}`}
                        onClick={() => {
                          setEditingTemplate(t);
                          setTemplateOpen(true);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Delete ${t.name}`}
                        onClick={() => void removeTemplate(t.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {canDesign && !owned && (
                    // Borrowed records offer Duplicate, never Edit - the rule
                    // every other signage surface follows. Editing a chain's
                    // design from one hotel would change what its siblings show.
                    <Tooltip title="Make your own copy">
                      <IconButton
                        size="small"
                        aria-label={`Duplicate ${t.name}`}
                        onClick={() => {
                          setEditingTemplate(null);
                          setTemplateOpen(true);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Row>
              );
            })
          )}
        </Stack>
      )}

      <SignageTemplateDialog
        key={editingTemplate?.id ?? "new-template"}
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        template={editingTemplate}
        onSave={saveTemplate}
        saving={saving}
        error={error}
      />
      <SignageSlideDialog
        key={editingSlide?.id ?? "new-slide"}
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        slide={editingSlide}
        templates={templates}
        onSave={saveSlide}
        saving={saving}
        error={error}
      />
    </PageContainer>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        p: 1.5,
        border: `1px solid ${tokens.divider}`,
        borderRadius: "8px",
        bgcolor: tokens.surface,
      }}
    >
      {children}
    </Stack>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: `1px dashed ${tokens.divider}`,
        borderRadius: "10px",
        p: 4,
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
        {children}
      </Typography>
    </Box>
  );
}
